import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import {
  EVENTS,
  getAuthorisationFields,
  getEndStateFields,
  STATES,
  USERS_ENDPOINT_URL,
} from './fields';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { convertFormlyDataToDto } from './util';
import { ToastService } from '@irembo-andela/irembogov3-common';
import { ServiceChangeRequestService } from 'src/app/core/services/service-change-request.service';
import { Subject, takeUntil } from 'rxjs';
import { DeleteConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'irembogov-edit-transition',
  templateUrl: './edit-transition.component.html',
  styleUrls: ['./edit-transition.component.scss'],
})
export class EditTransitionComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() public workflowCode: any;

  form = new FormGroup({});
  options: FormlyFormOptions = {};
  model: any = {};
  states: { label: string; value: string }[] = [];
  services: any[] = [];
  private destroy$ = new Subject<void>();
  fields: FormlyFieldConfig[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private toastService: ToastService,
    private modalService: NgbModal,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit() {
    // Set default states for initial rendering
    const defaultStates = STATES;
    this.states = [...defaultStates];

    const incomingAuth = this.workflowCode.authorisation;
    const incomingRoles: string[] = incomingAuth?.authorisedRoles ?? [];
    const isAnyRole =
      incomingRoles.length === 1 && incomingRoles[0] === 'ANY_ROLE';

    // Initialize model
    this.model = {
      transitionDetails: {
        startState: this.workflowCode.startState || '',
        name: this.workflowCode.name || '',
      },
      endStateOne: this.workflowCode.endStateOne || null,
      endStateTwo: this.workflowCode.endStateTwo || null,
      endStateCondition: null,
      authorisation: incomingAuth
        ? {
            authMode: incomingAuth.systemAuthorised
              ? 'SYSTEM_AUTHORIZED'
              : 'USER_AUTHORIZED',
            roleBased: incomingRoles.length > 0,
            anyAuthenticatedUser: isAnyRole,
            userBased: (incomingAuth.authorisedUsers?.length ?? 0) > 0,
            authorisedRoles: isAnyRole ? [] : incomingRoles,
            authorisedUsers: incomingAuth.authorisedUsers ?? [],
          }
        : {
            authMode: 'USER_AUTHORIZED',
            roleBased: false,
            anyAuthenticatedUser: false,
            userBased: false,
            authorisedRoles: [],
            authorisedUsers: [],
          },
    };

    this.options = {
      formState: {
        _rootModel: this.model,
        hideBuilderWrapper: true,
      },
    };

    this.transformApplyPayloadToArray(this.model.endStateOne);
    this.transformApplyPayloadToArray(this.model.endStateTwo);

    // Initialize fields with default states
    this.initializeFields();

    // Set up form change listener for APPLY_SERVICE fields
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateApplyServiceFields();
    });

    // Capture-phase listener catches scroll on any nested container (modal
    // body, formly stepper, window) and closes any open ng-select panel.
    document.addEventListener('scroll', this.closeOnScroll, true);

    // Hydrate the Users multi-select with previously-saved user records so
    // the chips render names/emails instead of raw UUIDs. The paginated
    // dropdown only knows about users on the current page; if a saved UUID
    // isn't on page 0, ng-select falls back to showing the raw value.
    // Backend `/auth/v1/search-users?ids=...` returns the matching records
    // in one shot.
    this.hydratePreSelectedUsers();
  }

  ngAfterViewInit() {
    // Force a change detection cycle after view initialization
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  initializeFields() {
    this.fields = [
      {
        type: 'tabs',
        props: {},
        expressions: {},
        validation: {
          messages: {},
        },
        fieldGroup: [
          {
            key: 'transitionDetails',
            props: {
              label: 'Transition Details',
              disabled: false,
              placeholder: '',
            },
            expressions: {},
            validation: {
              messages: {},
            },
            fieldGroup: [
              {
                type: 'block',
                props: {
                  label: 'State Details',
                  disabled: false,
                  placeholder: 'State Details',
                },
                expressions: {},
                validation: {
                  messages: {},
                },
                fieldGroup: [
                  {
                    key: 'startState',
                    type: 'customdropdownpaginated',
                    props: {
                      hint: '',
                      label: 'Start State',
                      options: [],
                      tooltip: '',
                      disabled: false,
                      multiple: false,
                      readonly: false,
                      required: true,
                      bindLabel: 'stateName',
                      bindValue: 'stateName',
                      placeholder: 'Select Start State',
                      defaultRequired: true,
                      useBaseUrl: true,
                      internalDescription: '',
                      dataset: {
                        url: '/application/v1/application-states/paginated',
                        params: { size: 50, page: 0 },
                        dataField: 'data.content',
                        pagination: 'true',
                        searching: 'true',
                        searchField: 'searchTerm',
                        sortDirection: 'asc',
                        sort: 'stateName',
                      },
                    },
                    defaultValue: null,
                    expressions: {
                      'props.required':
                        '!field?.props?.hideField && field?.props?.defaultRequired',
                    },
                    validation: {
                      messages: {
                        required: 'Start State is required',
                      },
                    },
                  },
                  {
                    key: 'name',
                    type: 'customdropdown',
                    props: {
                      hint: '',
                      label: 'Event',
                      options: EVENTS,
                      tooltip: '',
                      disabled: false,
                      multiple: false,
                      readonly: false,
                      required: true,
                      bindLabel: 'label',
                      bindValue: 'value',
                      placeholder: 'EVENT',
                      defaultRequired: true,
                      internalDescription: '',
                    },
                    defaultValue: null,
                    expressions: {
                      'props.required':
                        '!field?.props?.hideField && field?.props?.defaultRequired',
                    },
                    validation: {
                      messages: {
                        required: 'Event is Required',
                      },
                    },
                  },
                ],
              },
            ],
          },
          {
            key: 'endStateOne',
            props: {
              label: 'End State One',
              placeholder: '',
              disabled: false,
            },
            expressions: {},
            validation: {
              messages: {},
            },
            fieldGroup: getEndStateFields(
              'End State One',
              'endStateOne',
              this.states,
              this.services
            ),
          },
          {
            key: 'endStateTwo',
            props: {
              label: 'End State Two',
              placeholder: '',
              disabled: false,
            },
            expressions: {},
            validation: {
              messages: {},
            },
            fieldGroup: getEndStateFields(
              'End State Two',
              'endStateTwo',
              this.states,
              this.services
            ),
          },
          {
            key: 'authorisation',
            props: {
              label: 'Transition Authorization',
              placeholder: '',
              disabled: false,
              nextButtonText: 'Save Transition',
            },
            expressions: {},
            validation: {
              messages: {},
            },
            fieldGroup: getAuthorisationFields(),
          },
        ],
      },
    ];
  }

  /**
   * Pre-fetch user records for any UUIDs already stored in
   * `model.authorisation.authorisedUsers` and inject them into the Users
   * multi-select's options. Without this, ng-select can't resolve the
   * `bindLabel` for UUIDs that aren't on the current page and falls back
   * to displaying the raw UUID string in the chip.
   */
  private hydratePreSelectedUsers(): void {
    const ids: string[] = this.model?.authorisation?.authorisedUsers ?? [];
    if (!ids.length) return;

    let params = new HttpParams()
      .set('page', '0')
      .set('size', String(ids.length));
    ids.forEach(id => {
      params = params.append('ids', id);
    });

    const url = `${environment.apiGatewayBaseUrl}${USERS_ENDPOINT_URL}`;

    this.http
      .get<{ data: { content: Array<Record<string, unknown>> } }>(url, {
        params,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const users = res?.data?.content ?? [];
          if (!users.length) return;
          this.injectUsersIntoFieldOptions(users);
        },
        error: err => {
          // Non-fatal: dropdown still works, chips just show UUIDs.
          console.warn('Failed to hydrate pre-selected users:', err);
        },
      });
  }

  /**
   * Walk `this.fields` to find the `authorisedUsers` field config and
   * replace its `props.options` with the hydrated user list. The
   * `customdropdownpaginated` component subscribes to Formly's
   * `fieldChanges` stream and only refreshes ng-select's `items` when it
   * sees an `expressionChanges` event for `props.options` — direct
   * mutation alone is not enough, so we emit the event manually.
   */
  private injectUsersIntoFieldOptions(
    users: Array<Record<string, unknown>>
  ): void {
    let target: FormlyFieldConfig | null = null;
    const visit = (fields: FormlyFieldConfig[] | undefined): boolean => {
      if (!fields) return false;
      for (const f of fields) {
        if (f.key === 'authorisedUsers' && f.props) {
          target = f;
          return true;
        }
        if (visit(f.fieldGroup)) return true;
      }
      return false;
    };
    visit(this.fields);
    if (!target) return;

    const usersField = target as FormlyFieldConfig;
    if (usersField.props) {
      usersField.props.options = users;
    }

    // Notify the customdropdownpaginated subscription so it re-reads
    // `props.options` and pushes them into ng-select's `items` array.
    const fieldChanges = (
      this.options as FormlyFormOptions & {
        fieldChanges?: { next: (e: unknown) => void };
      }
    ).fieldChanges;
    fieldChanges?.next({
      field: usersField,
      type: 'expressionChanges',
      property: 'props.options',
      value: users,
    });

    this.cdr.detectChanges();
  }

  onLastStepEvent() {
    const { dto, errors } = convertFormlyDataToDto(this.model);

    if (errors.length > 0) {
      this.toastService.show({
        body: errors.join('\n'),
        type: 'warning',
      });
      return;
    }

    if (this.form.valid && dto) {
      this.form.markAllAsTouched();
      this.activeModal.close(dto);
    } else {
      // Handle the case where the form is invalid or dto is null
      this.form.markAllAsTouched();
    }
  }
  closeModal() {
    this.activeModal.dismiss('cancel');
  }

  // ng-select renders its dropdown panel at <body> level (appendTo='body' is
  // the default in the underlying IremboSelectComponent). When the user
  // scrolls inside the modal/form, ng-select doesn't reposition the panel —
  // it stays where it was opened, drifting away from the trigger and even
  // flipping above the field. Close any open panel on ANY scroll so the
  // user reopens it from the new trigger position. Scroll events don't
  // bubble, so we use the capture phase on document to catch them no matter
  // which container scrolled.
  private readonly closeOnScroll = (): void => {
    const openPanels = document.querySelectorAll('body > ng-dropdown-panel');
    if (openPanels.length === 0) return;
    document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
  };

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('scroll', this.closeOnScroll, true);
  }

  updateStateDropdowns() {
    this.initializeFields();

    this.options.updateInitialValue?.();

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  updateServiceDropdowns() {
    this.initializeFields();

    this.options.updateInitialValue?.();

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  updateApplyServiceFields() {
    const processEndState = (endState: any) => {
      if (!endState) return;

      const processActions = (actions: any[]) => {
        if (!actions) return;

        actions.forEach((action: any) => {
          if (
            action?.actionType === 'APPLY_SERVICE' &&
            action?.args?.serviceCode
          ) {
            // Get selected service ID
            const serviceCode = action.args.serviceCode;
            const selectedService = this.services.find(
              s => s.serviceCode === serviceCode
            );

            if (selectedService && selectedService.changeRequestId) {
              this.loadServiceFormFields(
                serviceCode,
                selectedService.changeRequestId
              );
            }
          }
        });
      };

      // Process both breaking and non-breaking actions
      if (endState.nonBreakingActions) {
        processActions(endState.nonBreakingActions);
      }

      if (endState.breakingActions) {
        processActions(endState.breakingActions);
      }
    };

    processEndState(this.model.endStateOne);
    processEndState(this.model.endStateTwo);
  }

  loadServiceFormFields(serviceCode: string, changeRequestId: string) {
    // Only load if we haven't already loaded this service's fields
    const cacheKey = `serviceCode`;
    if (this.model[cacheKey]) {
      return;
    }

    this.serviceChangeRequestService
      .getServiceChangeRequestFormDefinition(changeRequestId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response && response.data) {
            const fields: any[] = [];

            // Process service form definition to extract fields
            Object.values(response.data).forEach((sections: any) => {
              if (Array.isArray(sections)) {
                sections.forEach(section => {
                  if (section.fieldGroup) {
                    section.fieldGroup.forEach((formGroup: any) => {
                      if (formGroup.fieldGroup) {
                        formGroup.fieldGroup.forEach((block: any) => {
                          if (block.fieldGroup) {
                            block.fieldGroup.forEach((field: any) => {
                              if (field.key && field.props?.label) {
                                fields.push({
                                  fieldName: field.key,
                                  fieldLabel: field.props.label,
                                });
                              }
                            });
                          }

                          // Handle field arrays
                          if (block.fieldArray) {
                            if (block.fieldArray.fieldGroup) {
                              block.fieldArray.fieldGroup.forEach(
                                (field: any) => {
                                  if (field.key && field.props?.label) {
                                    fields.push({
                                      fieldName: field.key,
                                      fieldLabel: field.props.label,
                                    });
                                  }
                                }
                              );
                            }
                          }
                        });
                      }
                    });
                  }
                });
              }
            });

            this.model[cacheKey] = fields;
            this.form.updateValueAndValidity({ emitEvent: true });
            this.cdr.detectChanges();
          }
        },
        error: error => {
          console.error(
            `Error loading form fields for service ${serviceCode}:`,
            error
          );
        },
      });
  }

  transformApplyPayloadToArray(endState: any): void {
    if (!endState) return;

    const processActions = (actions: any[]) => {
      if (!actions) return;

      actions.forEach((action: any) => {
        if (
          action?.actionType === 'APPLY_SERVICE' &&
          action?.args?.applyPayload &&
          !Array.isArray(action.args.applyPayload)
        ) {
          // Transform object to array
          const payloadArray = [];

          for (const [key, value] of Object.entries(action.args.applyPayload)) {
            // Remove the '$.' prefix if present
            const cleanValue =
              typeof value === 'string' && value.startsWith('$.')
                ? value.substring(2)
                : value;

            payloadArray.push({
              key: key,
              value: cleanValue,
            });
          }

          // Replace the object with the array
          action.args.applyPayload = payloadArray;
        }
      });
    };

    // Process both breaking and non-breaking actions
    if (endState.nonBreakingActions) {
      processActions(endState.nonBreakingActions);
    }

    if (endState.breakingActions) {
      processActions(endState.breakingActions);
    }
  }

  deleteTransition() {
    const modalRef = this.modalService.open(DeleteConfirmationModalComponent, {
      centered: true,
      windowClass: 'delete-confirmation-modal',
    });

    modalRef.result.then((result: any) => {
      if (result === 'confirm') {
        this.activeModal.close('delete');
      }
    });
  }
}
